import type { BillingAccount } from '~/gql/billing/billing'
import { useMutation } from '@apollo/client/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_UPDATE_BILLING_PROFILE } from '~/gql/billing/billing'
import { client } from '~/helpers/apollo'

/** Champs de coordonnées, dans l'ordre d'une adresse de facturation. */
const FIELDS = [
  'companyName',
  'vatNumber',
  'addressLine1',
  'addressLine2',
  'postalCode',
  'city',
  'country',
  'billingEmail',
] as const

type Field = (typeof FIELDS)[number]
type FormState = Record<Field, string>

const EMPTY_FORM = Object.fromEntries(FIELDS.map(field => [field, ''])) as FormState

type Props = { account?: BillingAccount }

/**
 * Coordonnées de facturation, saisies par l'admin. Aucune validation fiscale :
 * elles seront reprises telles quelles par le paiement en ligne, et une
 * coordonnée incomplète n'empêche jamais rien.
 */
function BillingProfileForm({ account }: Props) {
  const { t } = useTranslation()
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [updateProfile, { loading }] = useMutation(GQL_UPDATE_BILLING_PROFILE, { client })

  useEffect(() => {
    if (!account) return
    setForm(Object.fromEntries(
      FIELDS.map(field => [field, account[field] ?? '']),
    ) as FormState)
  }, [account])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await updateProfile({ variables: { input: form } })
      toast.success(t('billing.profile.saved'))
    } catch {
      toast.error(t('billing.profile.error'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('billing.profile.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <p className="text-xs text-muted-foreground">{t('billing.profile.help')}</p>

          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(field => (
              <div key={field} className="flex flex-col gap-1.5">
                <Label htmlFor={`billing-${field}`}>{t(`billing.profile.${field}`)}</Label>
                <Input
                  id={`billing-${field}`}
                  type={field === 'billingEmail' ? 'email' : 'text'}
                  value={form[field]}
                  onChange={e => setForm(prev => ({ ...prev, [field]: e.target.value }))}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={loading}>{t('common.save')}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default BillingProfileForm
